import { useState, useEffect, useCallback, useRef } from "react";
import { api, formatApiError } from "@/lib/api";
import { safeUrl } from "@/lib/utils";
import { useAuth } from "@/context/AuthContext";
import { Heart, Eye, Instagram, Twitter, Youtube, VolumeX, Volume2, X, Compass, Sparkles, ChevronRight, Share2, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import ShareDiscoverModal from "@/components/ShareDiscoverModal";
import BoostPricingModal from "@/components/BoostPricingModal";

const CATEGORIES = [
  "All",
  "Food & Beverage",
  "Travel",
  "Real Estate",
  "Health & Fitness",
  "Beauty & Skincare",
  "Tech & Gadgets",
  "Home & Lifestyle",
  "Others",
];

const REGIONS = ["All", "US", "UK", "India", "Global"];

const PALETTES = [
  { bg: "#fef3c7", text: "#78350f", sub: "#92400e" },
  { bg: "#dbeafe", text: "#1e3a5f", sub: "#1d4ed8" },
  { bg: "#dcfce7", text: "#14532d", sub: "#15803d" },
  { bg: "#fce7f3", text: "#831843", sub: "#be185d" },
  { bg: "#ede9fe", text: "#4c1d95", sub: "#6d28d9" },
  { bg: "#ffedd5", text: "#7c2d12", sub: "#c2410c" },
];

const ROTATIONS = [-1.5, 1, -0.8, 1.5, 0, -1.2];




// ── Full-screen video player modal ─────────────────────────────
function VideoPlayerModal({ post, onClose }) {
  const videoRef = useRef(null);

  useEffect(() => {
    const v = videoRef.current;
    if (v) {
      v.muted = false;
      v.play().catch(() => { v.muted = true; v.play().catch(() => {}); });
    }
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  // Close on Escape
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <motion.div
      data-testid="discover-video-modal"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.18 }}
      className="fixed inset-0 z-[9999] bg-black flex flex-col items-center justify-center"
      onClick={onClose}
    >
      {/* Close button */}
      <button
        data-testid="discover-video-modal-close"
        onClick={onClose}
        className="absolute top-4 right-4 z-10 w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white backdrop-blur-sm transition-all"
      >
        <X size={18} />
      </button>

      {/* Video */}
      <div
        className="relative w-full max-w-sm mx-auto"
        style={{ height: "100dvh" }}
        onClick={(e) => e.stopPropagation()}
      >
        <video
          ref={videoRef}
          data-testid="discover-video-player"
          src={post.video_url}
          poster={post.thumbnail_url || undefined}
          controls
          playsInline
          loop
          className="w-full h-full object-contain"
        />

        {/* Info overlay at bottom */}
        <div className="absolute bottom-0 inset-x-0 pointer-events-none px-4 pb-6 pt-16 bg-gradient-to-t from-black/80 via-black/30 to-transparent">
          <div className="flex items-center gap-1.5 mb-1">
            <img src="/highrank-icon.webp" alt="HighRank" className="w-4 h-4 rounded-sm object-cover" draggable={false} />
            {post.category && (
              <span className="text-[10px] text-emerald-400 font-semibold uppercase tracking-wide">{post.category}</span>
            )}
          </div>
          <h3 className="text-white font-bold text-lg leading-tight">{post.business_name}</h3>
          {post.tagline && (
            <p className="text-white/70 text-sm mt-0.5 line-clamp-2">{post.tagline}</p>
          )}
        </div>
      </div>
    </motion.div>
  );
}

// ── Inline autoplay video ─────────────────────────────────────
function InlineVideo({ videoUrl, thumbnailUrl, onViewCount, paused }) {
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const [muted, setMuted] = useState(true);
  const [failed, setFailed] = useState(false);
  const viewedRef = useRef(false);

  // Pause/resume based on modal state
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    if (paused) { v.pause(); } else { v.play().catch(() => {}); }
  }, [paused]);

  useEffect(() => {
    const video = videoRef.current;
    const container = containerRef.current;
    if (!video || !container) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (paused || failed) return;
          if (entry.isIntersecting) {
            video.play().catch(() => setFailed(true));
            if (!viewedRef.current) {
              viewedRef.current = true;
              onViewCount?.();
            }
          } else {
            video.pause();
          }
        });
      },
      { threshold: 0.5 }
    );
    observer.observe(container);
    return () => observer.disconnect();
  }, [onViewCount, paused, failed]);

  const toggleMute = (e) => {
    e.stopPropagation();
    if (videoRef.current) {
      videoRef.current.muted = !videoRef.current.muted;
      setMuted(videoRef.current.muted);
    }
  };

  return (
    <div ref={containerRef} className="relative w-full h-full">
      <video
        ref={videoRef}
        src={videoUrl}
        poster={thumbnailUrl || undefined}
        muted
        loop
        playsInline
        preload="metadata"
        onError={() => setFailed(true)}
        className="w-full h-full object-cover"
      />
      {/* Mute/unmute button */}
      {!failed && (
        <button
          data-testid="discover-video-mute"
          onClick={toggleMute}
          className="absolute bottom-2 right-2 w-7 h-7 rounded-full bg-black/50 backdrop-blur-sm flex items-center justify-center text-white transition-all hover:bg-black/70 active:scale-90 touch-manipulation"
        >
          {muted ? <VolumeX size={12} /> : <Volume2 size={12} />}
        </button>
      )}
      {/* Fallback if autoplay blocked */}
      {failed && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/20">
          <span className="text-[10px] text-white bg-black/50 px-2 py-1 rounded-full">Tap to play</span>
        </div>
      )}
    </div>
  );
}

// ── Card ─────────────────────────────────────────────────────
function DiscoverCard({ post, index, onInstagramClick, onLike, liking, onViewCount, onShare, onVideoExpand, videoExpanded }) {
  const palette = PALETTES[index % PALETTES.length];
  const rotation = ROTATIONS[index % ROTATIONS.length];
  const isClickable = !!(post.instagram_link && !post.video_url) || !!post.video_url;

  const handleClick = () => {
    if (post.video_url) {
      onVideoExpand(post);
    } else if (post.instagram_link) {
      const url = safeUrl(post.instagram_link);
      if (url && url !== "#") {
        onInstagramClick(post);
        window.open(url, "_blank", "noopener,noreferrer");
      }
    }
  };

  return (
    <motion.div
      data-testid={`discover-card-${post.id}`}
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0, rotate: rotation }}
      whileHover={isClickable ? { rotate: 0, scale: 1.04, y: -6 } : {}}
      transition={{ duration: 0.3, delay: index * 0.04 }}
      style={{ backgroundColor: palette.bg }}
      className={`rounded-2xl overflow-visible shadow-md ${isClickable ? "cursor-pointer" : ""} relative`}
      onClick={handleClick}    >
      {/* Thumbtack pin */}
      <div
        className="absolute -top-2 left-1/2 -translate-x-1/2 z-10 w-4 h-4 rounded-full shadow-md border-2 border-white"
        style={{ backgroundColor: palette.text }}
      />

      <div className="rounded-2xl overflow-hidden">
      {/* Full-height media card — content overlaid at bottom */}
      <div className="relative aspect-[3/4] overflow-hidden" style={{ backgroundColor: palette.bg }}>
        {post.video_url ? (
          <InlineVideo
            videoUrl={post.video_url}
            thumbnailUrl={post.thumbnail_url || null}
            onViewCount={() => onViewCount(post.id)}
            paused={videoExpanded}
          />
        ) : post.thumbnail_url ? (
          <img src={post.thumbnail_url} alt={post.business_name} className="w-full h-full object-cover" loading={index < 4 ? "eager" : "lazy"} />
        ) : (
          <div className="w-full h-full flex items-center justify-center" style={{ backgroundColor: palette.bg }}>
            <span className="text-2xl font-black opacity-20" style={{ color: palette.text }}>{post.business_name?.charAt(0) || "?"}</span>
          </div>
        )}

        {/* HighRank logo badge — video cards + post/content link cards */}
        {(post.video_url || (post.instagram_link && !post.video_url && !/x\.com|twitter\.com|instagram\.com|youtube\.com|youtu\.be/i.test(post.instagram_link))) && (
          <img
            src="/highrank-icon.webp"
            alt="HighRank"
            className="absolute top-2 left-2 w-7 h-7 rounded-lg object-cover shadow-[0_2px_10px_rgba(0,0,0,0.45)]"
            draggable={false}
          />
        )}

        {/* Link icon — top right, adapts to link type */}
        {post.instagram_link && !post.video_url && (() => {
          const url = post.instagram_link;
          const isX = /x\.com|twitter\.com/i.test(url);
          const isIG = /instagram\.com/i.test(url);
          const isYT = /youtube\.com|youtu\.be/i.test(url);
          return (
            <div className="absolute top-2 right-2 w-6 h-6 rounded-full bg-white/90 flex items-center justify-center shadow-sm">
              {isX   && <Twitter size={12} style={{ color: "#000000" }} />}
              {isIG  && <Instagram size={12} style={{ color: "#e1306c" }} />}
              {isYT  && <Youtube size={12} style={{ color: "#FF0000" }} />}
              {!isX && !isIG && !isYT && <ExternalLink size={11} style={{ color: "#475569" }} />}
            </div>
          );
        })()}

        {/* Bottom overlay — gradient + all card info */}
        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent px-2.5 pt-8 pb-2.5">
          <div className="flex items-center gap-1.5 mb-0.5">
            {post.category && (
              <span className="text-[9px] font-semibold text-white/60 uppercase tracking-wide truncate flex-1">
                {post.category}
              </span>
            )}
            {post.region && (
              <span
                data-testid={`discover-card-region-${post.id}`}
                className="shrink-0 text-[8px] font-bold text-white/80 uppercase tracking-wider bg-white/15 backdrop-blur-sm rounded px-1.5 py-0.5 border border-white/10"
              >
                {post.region}
              </span>
            )}
          </div>
          <h3 className="font-bold text-sm leading-tight text-white truncate">{post.business_name}</h3>
          <p className="text-[11px] mt-0.5 line-clamp-1 leading-relaxed text-white/70">{post.tagline}</p>
          <div className="flex items-center gap-3 mt-1.5">
            <span className="flex items-center gap-1 text-[11px] text-white/55">
              <Eye size={11} />{post.view_count}
            </span>
            <button
              data-testid={`discover-like-btn-${post.id}`}
              onClick={(e) => { e.stopPropagation(); onLike(post.id); }}
              disabled={liking}
              className="flex items-center gap-1 text-[11px] transition-all active:scale-95 hover:scale-110 disabled:opacity-50 touch-manipulation"
              style={{ color: post.liked_by_me ? "#fb7185" : "rgba(255,255,255,0.55)" }}
            >
              <Heart size={11} fill={post.liked_by_me ? "currentColor" : "none"} />
              {post.like_count}
            </button>
            <button
              data-testid={`discover-share-btn-${post.id}`}
              onClick={(e) => { e.stopPropagation(); onShare(post); }}
              className="ml-auto flex items-center gap-1 text-[11px] text-white/55 hover:text-white/90 transition-all active:scale-95 touch-manipulation"
            >
              <Share2 size={11} />
            </button>
          </div>
        </div>
      </div>
    </div>
    </motion.div>
  );
}

// ── Request Modal ─────────────────────────────────────────────
// ── Main page ─────────────────────────────────────────────────
export default function Discover() {
  const { user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState("All");
  const [activeRegion, setActiveRegion] = useState("All");
  const [likingSet, setLikingSet] = useState(new Set());
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [sharePost, setSharePost] = useState(null);
  const [expandedVideo, setExpandedVideo] = useState(null);

  useEffect(() => {
    api.get("/discover")
      .then((r) => setPosts(r.data))
      .catch(() => toast.error("Failed to load posts"))
      .finally(() => setLoading(false));
  }, []);

  const filtered = posts
    .filter((p) => activeCategory === "All" || p.category === activeCategory)
    .filter((p) => activeRegion === "All" || p.region === activeRegion);

  const handleInstagramClick = useCallback(async (post) => {
    try { await api.post(`/discover/${post.id}/view`); } catch (_) {}
    setPosts((prev) => prev.map((p) => p.id === post.id ? { ...p, view_count: p.view_count + 1 } : p));
  }, []);

  const handleViewCount = useCallback(async (postId) => {
    try { await api.post(`/discover/${postId}/view`); } catch (_) {}
    setPosts((prev) => prev.map((p) => p.id === postId ? { ...p, view_count: p.view_count + 1 } : p));
  }, []);

  const handleLike = useCallback(async (postId) => {
    if (!user) { toast.error("Sign in to like posts"); return; }
    if (likingSet.has(postId)) return;
    setLikingSet((prev) => new Set(prev).add(postId));
    try {
      const r = await api.post(`/discover/${postId}/like`);
      setPosts((prev) =>
        prev.map((p) => p.id === postId ? { ...p, liked_by_me: r.data.liked, like_count: r.data.like_count } : p)
      );
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLikingSet((prev) => { const s = new Set(prev); s.delete(postId); return s; });
    }
  }, [user, likingSet]);

  return (
    <main data-testid="discover-page" className="pt-28 md:pt-16 min-h-screen">
      <section className="px-4 sm:px-6 py-10 max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-7">
          <div className="flex items-center gap-3 mb-2">
            <Compass size={26} className="text-emerald-400" />
            <h1 className="font-display text-3xl sm:text-4xl uppercase tracking-wide text-slate-100">Discover</h1>
          </div>
          <p className="text-slate-400 text-sm sm:text-base max-w-xl">
            Handpicked businesses worth knowing about — tap a card to explore.
          </p>
        </div>

        {/* Category filter */}
        <div className="relative mb-3">
          <div
            data-testid="discover-category-filter"
            className="flex gap-2 overflow-x-auto pb-2 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          >
            {CATEGORIES.map((cat) => (
              <button
                key={cat}
                data-testid={`discover-cat-${cat.replace(/\s+/g, "-").toLowerCase()}`}
                onClick={() => setActiveCategory(cat)}
                className={`shrink-0 px-3.5 py-1.5 rounded-full text-xs font-semibold border transition-all duration-200 ${
                  activeCategory === cat
                    ? "bg-emerald-500 border-emerald-500 text-black"
                    : "bg-white/5 border-white/10 text-slate-400 hover:border-white/20 hover:text-slate-200"
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
          <div className="pointer-events-none absolute right-0 top-0 bottom-2 w-12 bg-gradient-to-l from-white/80 to-transparent sm:hidden" />
        </div>

        {/* Region filter */}
        <div className="relative mb-7">
          <div
            data-testid="discover-region-filter"
            className="flex gap-2 overflow-x-auto pb-2 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          >
            {REGIONS.map((reg) => (
              <button
                key={reg}
                data-testid={`discover-region-${reg.toLowerCase()}`}
                onClick={() => setActiveRegion(reg)}
                className={`shrink-0 px-3.5 py-1.5 rounded-full text-xs font-semibold border transition-all duration-200 ${
                  activeRegion === reg
                    ? "bg-amber-400 border-amber-400 text-black"
                    : "bg-white/5 border-white/10 text-slate-400 hover:border-white/20 hover:text-slate-200"
                }`}
              >
                {reg === "All" ? "All Regions" : reg}
              </button>
            ))}
          </div>
        </div>

        {/* Loading skeleton */}
        {loading && (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="rounded-2xl bg-gray-200 animate-pulse" style={{ aspectRatio: "3/4" }} />
            ))}
          </div>
        )}

        {/* Empty state */}
        {!loading && filtered.length === 0 && (
          <div data-testid="discover-empty" className="text-center py-24">
            <Compass size={44} className="mx-auto text-slate-700 mb-4" />
            <p className="text-slate-500 text-lg font-medium">
              {activeCategory === "All" && activeRegion === "All"
                ? "Nothing here yet"
                : `No posts for ${[activeCategory !== "All" && activeCategory, activeRegion !== "All" && activeRegion].filter(Boolean).join(" · ")}`}
            </p>
            <p className="text-slate-600 text-sm mt-1">Check back soon — the team is curating posts.</p>
          </div>
        )}

        {/* Card grid */}
        {!loading && filtered.length > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5 sm:gap-6 pt-3">
            {filtered.map((post, i) => (
              <DiscoverCard
                key={post.id}
                post={post}
                index={i}
                onInstagramClick={handleInstagramClick}
                onViewCount={handleViewCount}
                onLike={handleLike}
                liking={likingSet.has(post.id)}
                onShare={setSharePost}
                onVideoExpand={setExpandedVideo}
                videoExpanded={expandedVideo?.id === post.id}
              />
            ))}
          </div>
        )}

        {/* Feature Your Business CTA */}
        <div className="mt-14 rounded-2xl border border-emerald-100 bg-gradient-to-br from-emerald-50 to-white overflow-hidden shadow-sm">
          <div className="px-6 py-8 sm:px-10 sm:py-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Sparkles size={18} className="text-amber-400" />
                <span className="text-xs font-semibold text-amber-400 uppercase tracking-widest">Get Featured</span>
              </div>
              <h2 className="text-xl sm:text-2xl font-black text-gray-900 mb-2">Want to appear on Discover?</h2>
              <p className="text-gray-600 text-sm max-w-md">
                Showcase your business to thousands of visitors. From <span className="text-emerald-400 font-semibold">$4.99</span>. Submit a request and we'll set you up.
              </p>
            </div>
            <button
              data-testid="discover-feature-cta"
              onClick={() => setShowRequestModal(true)}
              className="shrink-0 w-full sm:w-auto flex items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-400 text-black text-sm font-bold px-6 py-3 rounded-xl transition-all hover:shadow-[0_0_20px_rgba(16,185,129,0.35)]"
            >
              Feature My Business <ChevronRight size={16} />
            </button>
          </div>
        </div>
      </section>

      {/* Request modal — replaced by BoostPricingModal */}
      <BoostPricingModal
        open={showRequestModal}
        onOpenChange={setShowRequestModal}
        company={null}
      />

      {/* Video player modal */}
      <AnimatePresence>
        {expandedVideo && (
          <VideoPlayerModal post={expandedVideo} onClose={() => setExpandedVideo(null)} />
        )}
      </AnimatePresence>

      {/* Share modal */}
      {sharePost && (
        <ShareDiscoverModal
          open={!!sharePost}
          onOpenChange={(v) => { if (!v) setSharePost(null); }}
          post={sharePost}
        />
      )}
    </main>
  );
}
